import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import PageHeaderModal from "./Components/PageHeaderModal";
import api from "../../api";
import { IMAGE_URL } from "../../path";
import "../../styles/Login.scss";

interface Page {
    id: number;
    name: string;
    slug: string;
}

interface PageHeader {
    id: number;
    page_id: number;
    image: string;
    mobile_image: string | null;
    link: string | null;
    active: boolean;
    display_order: number;
    page: Page;
    created_at: string;
    updated_at: string;
}

const AdminPageHeaders = () => {
    const navigate = useNavigate();

    const [headers, setHeaders] = useState<PageHeader[]>([]);
    const [pages, setPages] = useState<Page[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");

    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedHeader, setSelectedHeader] =
        useState<PageHeader | null>(null);

    const [deletingId, setDeletingId] =
        useState<number | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError("");

            const [headersResponse, pagesResponse] =
                await Promise.all([
                    api.get("/admin/page-headers"),
                    api.get("/admin/pages"),
                ]);

            setHeaders(
                headersResponse.data.data ||
                headersResponse.data
            );

            setPages(
                pagesResponse.data.data ||
                pagesResponse.data
            );
        } catch (err: any) {
            console.error(
                "Failed to load page headers:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to load page headers. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchHeaders = async () => {
        try {
            setError("");

            const response = await api.get(
                "/admin/page-headers"
            );

            setHeaders(
                response.data.data ||
                response.data
            );
        } catch (err: any) {
            console.error(
                "Failed to refresh page headers:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to refresh page headers."
            );
        }
    };

    const handleAddHeader = () => {
        setSelectedHeader(null);
        setShowModal(true);
    };

    const handleEditHeader = (
        header: PageHeader
    ) => {
        setSelectedHeader(header);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedHeader(null);
    };

    const handleSaved = async () => {
        await fetchHeaders();
    };

    const handleDeleteHeader = async (
        header: PageHeader
    ) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete this ${header.page?.name || ""} page header?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingId(header.id);
            setError("");

            await api.delete(
                `/admin/page-headers/${header.id}`
            );

            await fetchHeaders();
        } catch (err: any) {
            console.error(
                "Failed to delete page header:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to delete page header. Please try again."
            );
        } finally {
            setDeletingId(null);
        }
    };

    const getImageUrl = (image: string) => {
        if (
            image.startsWith("http://") ||
            image.startsWith("https://")
        ) {
            return image;
        }

        return `${IMAGE_URL}/${image}`;
    };

    return (
        <div className="admin-page">

            <AdminHeader />

            <div className="admin-body">

                <AdminSidebar />

                <main className="admin-content">

                    <div className="admin-users">

                        <div className="admin-page-header">

                            <div>
                                <h1>
                                    Page Headers
                                </h1>

                                <p>
                                    Manage header images
                                    across the website.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="admin-primary-button"
                                onClick={handleAddHeader}
                            >
                                Add header
                            </button>

                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        <div className="admin-users-table">

                            {loading ? (
                                <p>
                                    Loading page headers...
                                </p>
                            ) : headers.length === 0 ? (
                                <p>
                                    No page headers found.
                                </p>
                            ) : (
                                <table>

                                    <thead>
                                    <tr>
                                        <th>
                                            Page
                                        </th>

                                        <th>
                                            Image
                                        </th>

                                        <th>
                                            Link
                                        </th>

                                        <th>
                                            Status
                                        </th>

                                        <th>
                                            Display order
                                        </th>

                                        <th>
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>

                                    <tbody>

                                    {headers.map(
                                        (header) => (
                                            <tr
                                                key={
                                                    header.id
                                                }
                                            >

                                                <td>
                                                    {header
                                                            .page
                                                            ?.name ||
                                                        "—"}
                                                </td>

                                                <td>
                                                    <img
                                                        src={getImageUrl(
                                                            header.image
                                                        )}
                                                        alt={`${header.page?.name || "Page"} header`}
                                                        style={{
                                                            width: "120px",
                                                            height: "60px",
                                                            objectFit:
                                                                "cover",
                                                            borderRadius:
                                                                "8px",
                                                        }}
                                                    />
                                                </td>

                                                <td>
                                                    {header.link ? (
                                                        <a
                                                            href={
                                                                header.link
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {
                                                                header.link
                                                            }
                                                        </a>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>

                                                <td>
                                                        <span
                                                            className={`admin-role ${
                                                                header.active
                                                                    ? "admin-role-admin"
                                                                    : "admin-role-editor"
                                                            }`}
                                                        >
                                                            {header.active
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </span>
                                                </td>

                                                <td>
                                                    {
                                                        header.display_order
                                                    }
                                                </td>

                                                <td>
                                                    <div className="admin-user-actions">

                                                        <button
                                                            type="button"
                                                            className="admin-table-button"
                                                            onClick={() =>
                                                                handleEditHeader(
                                                                    header
                                                                )
                                                            }
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="admin-table-button admin-table-button-danger"
                                                            onClick={() =>
                                                                handleDeleteHeader(
                                                                    header
                                                                )
                                                            }
                                                            disabled={
                                                                deletingId ===
                                                                header.id
                                                            }
                                                        >
                                                            {deletingId ===
                                                            header.id
                                                                ? "Deleting..."
                                                                : "Delete"}
                                                        </button>

                                                    </div>
                                                </td>

                                            </tr>
                                        )
                                    )}

                                    </tbody>

                                </table>
                            )}

                        </div>

                    </div>

                </main>

            </div>

            {showModal && (
                <PageHeaderModal
                    header={selectedHeader}
                    pages={pages}
                    onClose={handleCloseModal}
                    onSaved={handleSaved}
                />
            )}

        </div>
    );
};

export default AdminPageHeaders;
